if (!customElements.get('vat-toggle')) {
  /**
   * Switches prices between including and excluding VAT.
   *
   * Both bases are already in the markup and CSS shows one of them from data-vat-mode on the
   * html element, so switching is one attribute write - no page load, and nothing to fetch.
   *
   * The choice is remembered in localStorage, deliberately not on the cart. A cart attribute
   * would travel to checkout and print on the order as an additional detail, and a display
   * preference is not part of what was ordered.
   */
  class VatToggle extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      this.addEventListener('click', this.handleClick.bind(this));
      // Another toggle on the page, or a choice made in another tab, keeps this one in step.
      document.addEventListener('on:vat-mode:change', this.syncPressed.bind(this));

      // Liquid renders aria-pressed from the configured default, but the remembered choice is
      // applied to the html element before this runs - so correct it once on load, or a screen
      // reader is told the wrong basis is active.
      this.syncPressed();
    }

    /**
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const button = evt.target.closest('[data-vat-mode]');
      if (!button) return;

      const mode = button.dataset.vatMode;
      if (mode !== 'incl' && mode !== 'excl') return;
      if (mode === document.documentElement.dataset.vatMode) return;

      document.documentElement.dataset.vatMode = mode;

      try {
        window.localStorage.setItem('vatDisplay', mode);
      } catch (error) {
        // Private mode or blocked storage: the choice still applies for this page view.
      }

      document.dispatchEvent(new CustomEvent('on:vat-mode:change', { detail: { mode } }));
    }

    /**
     * Reflects the current mode on the buttons, for screen readers and for the active fill.
     */
    syncPressed() {
      const mode = document.documentElement.dataset.vatMode;

      this.querySelectorAll('[data-vat-mode]').forEach((button) => {
        button.setAttribute('aria-pressed', button.dataset.vatMode === mode ? 'true' : 'false');
      });
    }
  }

  customElements.define('vat-toggle', VatToggle);
}
